// Graceful shutdown + DOM/global cleanup for known embedded chat providers.
// Each entry: (1) calls the provider's teardown API while DOM is intact,
// (2) removes well-known DOM elements, (3) deletes known window globals so
// the provider's singleton guard doesn't block re-initialisation on the next
// show visit. All calls are isolated so a failure in one never blocks others.

/* eslint-disable @typescript-eslint/no-explicit-any */

const safe = (fn: () => void) => {
  try {
    fn();
  } catch {
    //
  }
};

const removeById = (id: string) => {
  document.getElementById(id)?.remove();
};

const deleteGlobals = (keys: string[]) => {
  const w = window as any;
  keys.forEach((k) => {
    try {
      delete w[k];
    } catch {
      //
    }
  });
};

const PROVIDER_CLEANUPS: Array<() => void> = [
  // Intercom
  // shutdown() removes window.Intercom itself but leaves intercomSettings behind
  () => {
    const w = window as any;
    if (typeof w.Intercom === "function") safe(() => w.Intercom("shutdown"));
    removeById("intercom-container");
    deleteGlobals(["Intercom", "intercomSettings"]);
  },

  // HubSpot Conversations
  // clear({resetWidget:true}) clears cookies + removes widget — the only full
  // teardown the SDK provides. widget.remove() alone is soft/reversible.
  () => {
    removeById("hubspot-messages-iframe-container");
    removeById("hubspot-conversations-inline-parent");
    removeById("hubspot-conversations-embed");
    document
      .querySelectorAll(
        'script[src*="hs-scripts.com"], script[src*="js.usemessages.com"]'
      )
      .forEach((el) => el.remove());
    // Named globals we know about
    deleteGlobals([
      "HubSpotConversations",
      "hsConversationsOnReady",
      "hsConversationsSettings",
    ]);
  },

  // Zoho SalesIQ
  () => {
    const w = window as any;
    if (w.$zoho?.salesiq) safe(() => w.$zoho.salesiq.reset());
    removeById("zsiqwidget");
    removeById("zsiqscript");
    deleteGlobals(["$zoho"]);
  },

  // Zoho Desk ASAP
  // invoke('close') is the documented call to close the widget window.
  // ZohoHCAsap / ZohoHCAsapSettings cover older HC-branding embed snippets.
  () => {
    const w = window as any;
    if (typeof w.ZohoDeskAsap?.invoke === "function")
      safe(() => w.ZohoDeskAsap.invoke("close"));
    removeById("zohohc-asap-web-viewer");
    deleteGlobals([
      "ZohoDeskAsap",
      "ZohoHCAsap",
      "ZohoHCAsapSettings",
      "ZohoDeskAsapReady",
    ]);
  },

  // LiveChat (livechat.com / Text Platform)
  // destroy() permanently blocks re-init by design; deleting all known globals
  // (including internal init-state flags) lets a re-injected script start fresh.
  () => {
    const w = window as any;
    if (w.LiveChatWidget) safe(() => w.LiveChatWidget.call("destroy"));
    removeById("chat-widget-container");
    deleteGlobals([
      "LiveChatWidget",
      "__lc",
      "LC_API",
      "LC_Invite",
      "__lc_inited",
      "__lc_iframe_current_skill",
      "__lc_lang",
    ]);
  },

  // Freshchat (fcWidget — standalone chat SDK)
  () => {
    const w = window as any;
    if (w.fcWidget) safe(() => w.fcWidget.destroy());
    removeById("fc_widget");
    removeById("fc_frame");
    deleteGlobals(["fcWidget", "fcSettings", "fcWidgetMessengerConfig"]);
  },

  // FreshworksWidget (Freshdesk help widget — ticket + chat combo)
  () => {
    const w = window as any;
    if (typeof w.FreshworksWidget === "function")
      safe(() => w.FreshworksWidget("destroy"));
    removeById("freshworks-container");
    removeById("freshworks-frame");
    deleteGlobals(["FreshworksWidget", "fwSettings"]);
  },

  // Salesforce Enhanced Messaging for Web (embeddedservice_bootstrap)
  // Documented SPA teardown: clearSession(true) clears all session data (incl.
  // authenticated), removeAllComponents() tears down all injected UI.
  // The bootstrap object is intentionally NOT deleted — the Salesforce SPA
  // re-init pattern calls init() on the surviving object after removeAllComponents().
  () => {
    const w = window as any;
    if (w.embeddedservice_bootstrap?.userVerificationAPI)
      safe(() =>
        w.embeddedservice_bootstrap.userVerificationAPI.clearSession(true)
      );
    if (w.embeddedservice_bootstrap?.utilAPI)
      safe(() => w.embeddedservice_bootstrap.utilAPI.removeAllComponents());
    document.querySelector("embedded-service-help-button")?.remove();
    removeById("embedded-messaging");
  },

  // Salesforce Legacy snap-ins (embedded_svc)
  () => {
    const w = window as any;
    if (w.embedded_svc?.liveAgentAPI)
      safe(() => w.embedded_svc.liveAgentAPI.endChat());
    removeById("embedded-service");
    deleteGlobals(["embedded_svc"]);
  },

  // LiveAgent (ladesk.com) — no official destroy; clear instance collections
  // per the documented "Change button on demand without page reload" pattern,
  // and remove the script tag the SDK injects (id: la_x2s6df8d).
  () => {
    const w = window as any;
    if (w.LiveAgent?.instance) {
      safe(() => {
        w.LiveAgent.instance.elements = [];
        w.LiveAgent.instance.widgets = [];
      });
    }
    removeById("la_x2s6df8d");
    deleteGlobals(["LiveAgent"]);
  },

  // Pipedrive LeadBooster — no destroy API; close the chat window, remove the
  // iframe container, and clear both the instance and pre-load config globals.
  () => {
    const w = window as any;
    if (w.LeadBooster) safe(() => w.LeadBooster.trigger("close"));
    removeById("LeadboosterContainer");
    deleteGlobals(["LeadBooster", "pipedriveLeadboosterConfig"]);
  },
];

export const runEmbeddedChatProviderCleanup = (): void => {
  PROVIDER_CLEANUPS.forEach(safe);
};
